import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Music, Upload, Trash2, GripVertical, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface ExampleWork {
  id: string;
  title: string;
  file_url: string;
  file_size_bytes: number;
  duration_seconds: number | null;
  display_order: number;
}

interface MentorExampleWorksManagerProps {
  mentorId: string;
  className?: string;
}

export function MentorExampleWorksManager({ mentorId, className }: MentorExampleWorksManagerProps) {
  const [works, setWorks] = useState<ExampleWork[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchWorks();
  }, [mentorId]);

  const fetchWorks = async () => {
    try {
      const { data, error } = await supabase
        .from('mentor_example_works')
        .select('*')
        .eq('mentor_id', mentorId)
        .order('display_order');
      
      if (error) throw error;
      setWorks(data || []);
    } catch (error: any) {
      console.error('Error fetching example works:', error);
      toast({
        title: 'Error',
        description: 'Failed to load example works',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.includes('audio/mpeg') && !file.name.toLowerCase().endsWith('.mp3')) {
      toast({
        title: 'Invalid File Type',
        description: 'Only MP3 files are allowed',
        variant: 'destructive'
      });
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: 'File Too Large',
        description: 'Maximum file size is 10MB',
        variant: 'destructive'
      });
      return;
    }

    // Check if at limit
    if (works.length >= 5) {
      toast({
        title: 'Maximum Reached',
        description: 'You can only upload up to 5 example works',
        variant: 'destructive'
      });
      return;
    }

    try {
      setUploading(true);

      // Generate unique filename
      const timestamp = Date.now();
      const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const filePath = `${mentorId}/${timestamp}-${sanitizedName}`;

      // Upload to storage
      const { error: uploadError } = await supabase.storage
        .from('mentor-audio-examples')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('mentor-audio-examples')
        .getPublicUrl(filePath);

      // Insert record
      const { error: insertError } = await supabase
        .from('mentor_example_works')
        .insert({
          mentor_id: mentorId,
          title: file.name.replace(/\.[^/.]+$/, ''), // Remove extension
          file_url: publicUrl,
          file_size_bytes: file.size,
          display_order: works.length
        });

      if (insertError) throw insertError;

      toast({
        title: 'Success',
        description: 'Example work uploaded successfully'
      });

      fetchWorks();
    } catch (error: any) {
      console.error('Error uploading file:', error);
      toast({
        title: 'Upload Failed',
        description: error.message || 'Failed to upload file',
        variant: 'destructive'
      });
    } finally {
      setUploading(false);
      e.target.value = ''; // Reset input
    }
  };

  const handleDelete = async (work: ExampleWork) => {
    try {
      // Extract file path from URL
      const urlParts = work.file_url.split('/mentor-audio-examples/');
      const filePath = urlParts[1];

      // Delete from storage
      if (filePath) {
        await supabase.storage
          .from('mentor-audio-examples')
          .remove([filePath]);
      }

      // Delete record
      const { error } = await supabase
        .from('mentor_example_works')
        .delete()
        .eq('id', work.id);

      if (error) throw error;

      toast({
        title: 'Deleted',
        description: 'Example work removed successfully'
      });

      fetchWorks();
    } catch (error: any) {
      console.error('Error deleting work:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete example work',
        variant: 'destructive'
      });
    }
  };

  const handleTitleUpdate = async (workId: string, newTitle: string) => {
    try {
      const { error } = await supabase
        .from('mentor_example_works')
        .update({ title: newTitle })
        .eq('id', workId);

      if (error) throw error;

      setWorks(works.map(w => w.id === workId ? { ...w, title: newTitle } : w));
    } catch (error: any) {
      console.error('Error updating title:', error);
      toast({
        title: 'Error',
        description: 'Failed to update title',
        variant: 'destructive'
      });
    }
  };

  if (loading) {
    return (
      <Card className={className}>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Music className="w-5 h-5" />
          Example Works
        </CardTitle>
        <CardDescription>
          Upload 1-5 MP3 files to showcase your work (max 10MB each)
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {works.map((work) => (
          <div key={work.id} className="flex items-center gap-3 p-3 border rounded-lg">
            <GripVertical className="w-4 h-4 text-muted-foreground" />
            <Music className="w-5 h-5 text-primary" />
            <Input
              value={work.title}
              onChange={(e) => handleTitleUpdate(work.id, e.target.value)}
              className="flex-1"
              placeholder="Title"
            />
            <span className="text-sm text-muted-foreground">
              {(work.file_size_bytes / (1024 * 1024)).toFixed(1)}MB
            </span>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleDelete(work)}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ))}

        {works.length < 5 && (
          <div>
            <input
              type="file"
              accept=".mp3,audio/mpeg"
              onChange={handleFileUpload}
              className="hidden"
              id="audio-upload"
              disabled={uploading}
            />
            <label htmlFor="audio-upload">
              <Button
                variant="outline"
                className="w-full"
                disabled={uploading}
                asChild
              >
                <span>
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload MP3 ({works.length}/5)
                    </>
                  )}
                </span>
              </Button>
            </label>
          </div>
        )}

        {works.length === 0 && !uploading && (
          <p className="text-center text-sm text-muted-foreground py-4">
            No example works uploaded yet
          </p>
        )}
      </CardContent>
    </Card>
  );
}
